import React, {useEffect, useState} from 'react'
import {TherapyCardStyled} from './TherapyCardStyled'
import {Checkbox, DatePicker, Form, Input, Tag, Tabs} from 'antd'
import locale from 'antd/es/date-picker/locale/it_IT'
import {Snackbar, Typography} from '@mui/material'
import moment from 'moment'
import {STATUS} from 'src/constants/constants'
import SnackbarAlertCustom from 'src/components/elements/SnackbarAlertCustom/SnackbarAlertCustom'
import apiClient from 'src/services/apiClient'
import {useAppSelector} from 'src/hooks'
import _ from 'lodash'
import PrimaryButton from 'src/components/elements/Buttons/PrimaryButton/PrimaryButton'
import {FormattedMessage} from 'react-intl'
import TabsCustom from 'src/components/layouts/TabsLayout/TabsLayout'
import CardCustom from 'src/components/elements/CardCustom/CardCustom'
import Loader from 'src/components/elements/Loader/Loader'
import WarningPatientEmptyData from 'src/components/elements/WarningEmptyCard/WarningPatientEmptyData'
import SecondaryTherapyCardTabs from 'src/components/templates/TherapyDialogs/SecondaryTherapyCardTabs'
import {formatTherapyState} from 'src/components/templates/TherapyDialogs/therapyFormatters'
import {resolveSecondaryTherapyDataSchema} from 'src/constants/secondaryTherapyFields'

const THERAPY_DEVICE_OF_ASSUMPTIONS : any = {
  PEN: 'Penna',
  SYRINGE: 'Siringa preriempita',
  PUMP: 'Pompa',
  INHALER: 'Inalatore'
}

const formatUnitOfMeasure = (dosage: any = {}) => {
  return dosage.value ? `${dosage.value} ${dosage.unitType}` : ''
}

const formatDevice = (deviceKey: string) => THERAPY_DEVICE_OF_ASSUMPTIONS[deviceKey] ?? deviceKey

const TherapyCard: React.FC<{patientId: string}> = ({patientId}) => {
  const {patientDetailConfiguration: {therapiesConfiguration, secondaryTherapiesConfiguration, tabs}} = useAppSelector(state => state.pspConfigReducer.data)
  const secondaryTherapiesDataSchema = resolveSecondaryTherapyDataSchema(secondaryTherapiesConfiguration) ?? []
  const {userGroup} = useAppSelector(state => state.userReducer.data)

  // "secondaryTherapy" è un toggle opt-out: prima che esistesse questo campo la scheda "Terapie
  // accessorie" era sempre visibile, quindi in assenza del campo (programmi non ancora
  // riconfigurati) resta visibile di default.
  const showPrimaryTherapies = Boolean(tabs?.therapy?.show)
  const showSecondaryTherapies = tabs?.secondaryTherapy?.show !== false

  const [form] = Form.useForm()
  const [data, setData] = useState<any[]>([])
  const [therapyIndex, setTherapyIndex] = useState<number>()
  const [selectedTherapy, setSelectedTherapy] = useState<any>()
  const [status, setStatus] = useState<any>()
  const [statusPatch, setStatusPatch] = useState<any>(undefined)

  const [secondaryTherapies, setSecondaryTherapies] = useState<any[]>([])
  const [statusSecondaryTherapies, setStatusSecondaryTherapies] = useState<any>()

  const editableFields = ['introductoryMeetingNotes']

  const fetchData = async () => {
    const therapies = await apiClient.getPatientsTherapies(patientId, setStatus)
    const sortedTherapies = therapies.sort((a: any, b: any) => {
      const statusA = getTherapyStatus(a)
      const statusB = getTherapyStatus(b)
      const statusOrder: {[key: string]: number} = {FUTURE: 0, ACTIVE: 1, PAST: 2}

      const statusDiff = statusOrder[statusA] - statusOrder[statusB]
      if (statusDiff !== 0) return statusDiff

      const dateA = moment(a.startDate)
      const dateB = moment(b.startDate)

      if (statusA === 'FUTURE' || statusA === 'ACTIVE') {
        return dateA.diff(dateB)
      } else {
        return dateB.diff(dateA)
      }
    })
    setData(sortedTherapies)

    const activeIndex = sortedTherapies.findIndex((t: any) => getTherapyStatus(t) === 'ACTIVE')
    setTherapyIndex(activeIndex >= 0 ? activeIndex : 0)
  }

  useEffect(() => {
    fetchData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId])

  const fetchSecondaryTherapies = async () => {
    const result = await apiClient.getPatientsSecondaryTherapies(patientId, setStatusSecondaryTherapies) ?? []
    setSecondaryTherapies(result)
  }

  useEffect(() => {
    fetchSecondaryTherapies()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId])

  useEffect(() => {
    therapiesConfiguration.dataSchema.forEach((element: any) => {
      if (element.type === 'date') {
        form.setFieldValue(element.value, selectedTherapy && _.get(selectedTherapy, element.value) ? moment(_.get(selectedTherapy, element.value)) : undefined)
      } else {
        let formValue = selectedTherapy ? _.get(selectedTherapy, element.value) : undefined
        if (element.type === 'valueAndUnitType' || (element.type === 'assumptionDosage' && _.get(selectedTherapy, 'assumptionDosage'))) {
          formValue = formatUnitOfMeasure(formValue)
        } else if (element.type === 'device') {
          formValue = formatDevice(formValue)
        }
        form.setFieldValue(element.value, formValue)
      }
    })
  }, [form, selectedTherapy, therapiesConfiguration])

  useEffect(() => {
    if (data.length > 0 && therapyIndex !== undefined) {
      setSelectedTherapy(data[therapyIndex])
    }
  }, [data, therapyIndex])

  const handleEdit = async (values: any, id: string) => {
    const patchBody = editableFields.map(field => ({[field]: values[field]})).reduce((acc, curr) => ({...acc, ...curr}), {})
    await apiClient.patchPatientTherapy(id, patchBody, setStatusPatch)
    await fetchData()
  }

  const getTherapyStatus = (therapy: any) => {
    const today = moment()
    const startDate = moment(therapy.startDate)
    const endDate = therapy.endDate ? moment(therapy.endDate) : null

    if (today.isBetween(startDate, endDate, null, '[]') || therapy.state === 'ACTIVE') {
      return 'ACTIVE'
    } else if (endDate && today.isAfter(endDate)) {
      return 'PAST'
    } else if (today.isBefore(startDate)) {
      return 'FUTURE'
    }
    return 'UNKNOWN'
  }

  const propertyIsInDataSchema = (key: string) => {
    return therapiesConfiguration.dataSchema.some((element: any) => element.value === key)
  }

  const propertyInDataSchemaName = (key: string) => {
    return therapiesConfiguration.dataSchema.find((element: any) => element.value === key).name
  }

  const isDisabled = (field: string, group: string) => {
    if (group.toUpperCase().includes('DOCTOR') && editableFields.includes(field)) {
      return false
    }
    return true
  }

  const createTherapyContent = () => {
    let dataSchema = therapiesConfiguration.dataSchema
    if (selectedTherapy && therapiesConfiguration.dataSchema.some((element: any) => element.type === 'assumptionDosage' && !_.get(selectedTherapy, 'assumptionDosage'))) {
      dataSchema = therapiesConfiguration.dataSchema.filter((element: any) => ![
        'assumptionDosage.infusionRate',
        'assumptionDosage.secondaryDosage',
        'assumptionDosage.dilution',
        'assumptionDosage'
      ].includes(element.value))
    }
    return dataSchema.map((element : any) => {
      if (element.type === 'date') {
        return (
          <Form.Item
            key={element.value}
            label={element.name}
            name={element.value}
          >
            <DatePicker
              disabled={isDisabled(element.value, userGroup)}
              format='ddd DD MMM YYYY'
              getPopupContainer={(trigger) => trigger}
              locale={locale}
              placeholder=''
            />
          </Form.Item>
        )
      } else if (element.type === 'boolean') {
        return (
          <Form.Item
            key={element.value}
            label={element.name}
            name={element.value}
            valuePropName='checked'
          >
            <Checkbox disabled={isDisabled(element.value, userGroup)} />
          </Form.Item>
        )
      } else if (element.type === 'text-area') {
        return (
          <Form.Item
            key={element.value}
            label={element.name}
            name={element.value}
          >
            <Input.TextArea disabled={isDisabled(element.value, userGroup)} />
          </Form.Item>
        )
      } else {
        return (
          <Form.Item
            key={element.value}
            label={element.name}
            name={element.value}
          >
            <Input disabled={isDisabled(element.value, userGroup)} />
          </Form.Item>
        )
      }
    })
  }

  const therapiesContent = (
    <>
      {data.length > 0 && (
        <Tabs
          activeKey={therapyIndex?.toString()}
          className='therapy-selector-tabs'
          items={data.map((therapy, index) => {
            const therapyStatus = getTherapyStatus(therapy)
            let tagContent = null

            if (therapyStatus === 'ACTIVE') {
              tagContent = <Tag color='success'>{'Attiva'}</Tag>
            } else if (therapyStatus === 'PAST') {
              tagContent = <Tag color='red'>{'Precedente'}</Tag>
            } else if (therapyStatus === 'FUTURE') {
              tagContent = <Tag color='processing'>{'Futura'}</Tag>
            }

            return {
              key: index.toString(),
              label: (
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <span>{therapy.name}</span>
                  {tagContent}
                </div>
              )
            }
          })}
          onChange={(key) => setTherapyIndex(parseInt(key))}
        />
      )}
      <Form
        form={form}
        id='therapy-form'
        layout='vertical'
      >
        {createTherapyContent()}
      </Form>
      <div>
        {selectedTherapy && therapiesConfiguration.dataSchema.some((element: any) => element.type === 'assumptionDosage' && !_.get(selectedTherapy, 'assumptionDosage')) &&
          <>
            <div style={{
              padding: '0px 16px'
            }}
            ><b>{'Dosaggi'}</b>
            </div>
            {_.get(selectedTherapy, 'customDosages')?.map((dosage: any, index: number) => {
              return (
                <div
                  key={index}
                  style={{
                    padding: '16px 16px 0px 16px'
                  }}
                >
                  <div>{'Assunzione ' + (index + 1)}</div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: '8px',
                      padding: '16px 0px'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        fontSize: '12px'
                      }}
                    >
                      <div>{'Dosaggio'}
                      </div>
                      <Input disabled style={{color: '#555'}} value={formatUnitOfMeasure(dosage)} />
                    </div>
                    {propertyIsInDataSchema('assumptionDosage.infusionRate') &&
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          fontSize: '12px'
                        }}
                      >
                        <div>{propertyInDataSchemaName('assumptionDosage.infusionRate')}</div>
                        <Input disabled style={{color: '#555'}} value={formatUnitOfMeasure(dosage.infusionRate)} />
                      </div>}
                    {propertyIsInDataSchema('assumptionDosage.secondaryDosage') &&
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          fontSize: '12px'
                        }}
                      >
                        <div>{propertyInDataSchemaName('assumptionDosage.secondaryDosage')}</div>
                        <Input disabled style={{color: '#555'}} value={formatUnitOfMeasure(dosage.secondaryDosage)} />
                      </div>}
                    {propertyIsInDataSchema('assumptionDosage.dilution') &&
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          fontSize: '12px'
                        }}
                      >
                        <div>{propertyInDataSchemaName('assumptionDosage.dilution')}</div>
                        <Input disabled style={{color: '#555'}} value={dosage.dilution} />
                      </div>}
                  </div>
                </div>
              )
            })}
          </>}
      </div>
      <div className='save-button-container'>
        <PrimaryButton
          key='save'
          onClick={() => handleEdit(form.getFieldsValue(), selectedTherapy._id)}
        >
          <FormattedMessage id='save' />
        </PrimaryButton>
      </div>
    </>
  )

  const secondaryTherapiesContent = (
    <div style={{padding: '16px'}}>
      {statusSecondaryTherapies === STATUS.PENDING && <Loader />}
      {statusSecondaryTherapies !== STATUS.PENDING && secondaryTherapies.length === 0 &&
        <WarningPatientEmptyData message='Nessuna terapia accessoria trovata' />}
      <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
        {secondaryTherapies.map((secondaryTherapy: any) => {
          // Transitional fallback to the legacy `drug.state` for documents that predate the root
          // `state` field (RF18 of therapy-service specs/2026-09-notification-secondaryTherapy.md)
          // and haven't been backfilled yet.
          const state = secondaryTherapy.state ?? secondaryTherapy.drug?.state
          return (
            <CardCustom
              key={secondaryTherapy._id}
              title={
                <div style={{display: 'flex', alignItems: 'baseline', gap: '8px'}}>
                  {secondaryTherapy.drug?.name ?? 'Terapia accessoria'}
                  {state && (
                    <Tag color={state === 'NOT_ACTIVE' ? 'red' : 'success'}>
                      {formatTherapyState(state)}
                    </Tag>
                  )}
                </div>
              }
            >
              <SecondaryTherapyCardTabs secondaryTherapy={secondaryTherapy} dataSchema={secondaryTherapiesDataSchema} />
            </CardCustom>
          )
        })}
      </div>
    </div>
  )

  const therapyTabItems = [
    showPrimaryTherapies && {
      label: <Typography style={{fontSize: 12, fontWeight: 600}}>{'Terapie'}</Typography>,
      content: therapiesContent
    },
    showSecondaryTherapies && {
      label: <Typography style={{fontSize: 12, fontWeight: 600}}>{'Altre terapie'}</Typography>,
      content: secondaryTherapiesContent
    }
  ].filter(Boolean) as {label: JSX.Element, content: JSX.Element}[]

  return (
    <TherapyCardStyled>
      <TabsCustom
        contents={therapyTabItems.map((item) => item.content)}
        tabs={therapyTabItems.map((item) => ({label: item.label}))}
      />
      <Snackbar
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        autoHideDuration={2000}
        onClose={() => { setStatus(undefined) }}
        open={status === STATUS.REJECTED || statusPatch === STATUS.REJECTED}
      >
        <div>
          <SnackbarAlertCustom
            onClose={() => { setStatus(undefined) }}
            severity='error'
            sx={{width: '100%'}}
          >
            <Typography variant='h5'>
              {'Errore nel caricare i dati'}
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
    </TherapyCardStyled>
  )
}

export default TherapyCard
